<template>
  <div class="doc-reader">
    <div v-if="pending" class="doc-state">
      <ProgressSpinner style="width: 40px; height: 40px" />
    </div>

    <Message v-else-if="error" severity="error" :closable="false">
      {{ t('authoredDocuments.readFailed') }}
    </Message>

    <Message v-else-if="!row" severity="warn" :closable="false">
      {{ t('authoredDocuments.notFound') }}
    </Message>

    <template v-else>
      <div class="doc-header">
        <div class="min-w-0">
          <div class="flex items-center gap-2 flex-wrap">
            <h1 class="text-xl font-semibold m-0">{{ row.title }}</h1>
            <!-- AI Act Art. 50(2): this is chat-authored content, and the
                 chat-window carve-out does not travel with it. Beside the title,
                 like every other surface — it qualifies the thing it sits next to. -->
            <AiGeneratedTag />
            <!-- The same attachment the library row offers, on the page where the
                 document is actually being read. On the TITLE line rather than among
                 the header actions: it is about this document, like the chip beside
                 it, where Export is about getting a file out. Hidden inside the
                 chat's own side panel (`canAsk`) — there the conversation is already
                 on screen, and a button to open the chat you are looking at is noise. -->
            <AskAssistantButton
              v-if="canAsk"
              icon-only
              :label="t('authoredDocuments.library.ask_label')"
              :focus="documentFocus"
            />
          </div>
          <div class="doc-meta mt-1">
            <AuthoredDocumentStatusTag :state="row.lifecycle_state" />
            <span class="text-sm text-surface-600">{{
              t('authoredDocuments.revision', { n: row.revision })
            }}</span>
          </div>
        </div>
        <div class="doc-actions">
          <Button
            icon="pi pi-download"
            :label="t('authoredDocuments.export.label')"
            severity="secondary"
            outlined
            aria-haspopup="true"
            aria-controls="authored-document-export-menu"
            @click="exportMenu?.toggle($event)"
          />
          <Menu id="authored-document-export-menu" ref="exportMenu" :model="exportItems" popup />
          <!-- Rename / status / delete (MYAC-1375) — on the document page only
               (`canManage`). Inside the chat's panel the conversation is the way to
               change a document, and a delete there would pull the page out from
               under the chat that is showing it. -->
          <AuthoredDocumentActionsMenu
            v-if="canManage"
            :state="row.lifecycle_state"
            :busy="busy"
            @rename="emit('rename')"
            @set-status="emit('set-status', $event)"
            @delete="emit('delete')"
          />
        </div>
      </div>
      <Card :pt="{ body: { class: '!p-0' }, content: { class: '!p-0' } }">
        <template #content>
          <div class="p-6" data-testid="document-body">
            <Markdown :content="body" />
          </div>
        </template>
      </Card>
    </template>
  </div>
</template>

<script setup lang="ts">
/**
 * The one way an authored document is read on screen — header, states, body, exports.
 *
 * Presentational on purpose: it takes the row rather than subscribing, so the three
 * surfaces of MYAC-1162 (this page, the chat side panel, the library) each own their
 * own subscription and gating while rendering identically, and so a test can mount it
 * with a plain object. The subscription lives in `useAuthoredDocument`.
 *
 * Per the views-store rules: error and not-found are distinct states, and "not found"
 * renders only after a successful (empty) read — `pending` is checked first.
 */
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import Button from 'primevue/button'
import Card from 'primevue/card'
import Menu from 'primevue/menu'
import Message from 'primevue/message'
import ProgressSpinner from 'primevue/progressspinner'
import AiGeneratedTag from '@/components/shared/AiGeneratedTag.vue'
import AuthoredDocumentActionsMenu from './AuthoredDocumentActionsMenu.vue'
import AuthoredDocumentStatusTag from './AuthoredDocumentStatusTag.vue'
import AskAssistantButton from '@/components/chat/AskAssistantButton.vue'
import Markdown from '@/components/shared/Markdown.vue'
import type { AuthoredDocumentContentRow } from '@/composables/useAuthoredDocument'
import type { AuthoredDocumentStatus } from '@/composables/useAuthoredDocumentActions'
import { useAuthoredDocumentExport } from '@/composables/useAuthoredDocumentExport'
import { AUTHORED_DOCUMENT_EXPORTS } from '@/composables/authoredDocumentExportOptions'
import { documentBodyForDisplay } from './documentBody'
import { authoredDocumentFocus } from './documentFocus'

const props = withDefaults(
  defineProps<{
    row: AuthoredDocumentContentRow | null
    pending?: boolean
    error?: Error | null
    /** Offer the assistant on this document. False inside the chat's own panel. */
    canAsk?: boolean
    /** Offer rename / status / delete. Only the document page does. */
    canManage?: boolean
    /** An action on this document is in flight. */
    busy?: boolean
  }>(),
  { pending: false, error: null, canAsk: true, canManage: false, busy: false },
)

/** What the user asked for; the page owns the dialogs and the commands. */
const emit = defineEmits<{
  rename: []
  'set-status': [status: AuthoredDocumentStatus]
  delete: []
}>()

const { t } = useI18n()

/** Built where the row builds it, so the two attach the same document alike. */
const documentFocus = computed(() =>
  authoredDocumentFocus(
    props.row?.doc_id ?? '',
    props.row?.title,
    t('authoredDocuments.library.untitled'),
  ),
)
const { runExport } = useAuthoredDocumentExport()

// The chat routinely opens a document with an h1 repeating the title it also set as
// metadata, so the header and the body would say the same thing twice. Display only:
// `downloadMarkdown` keeps the raw body, because a standalone .md file has no header
// and that heading is the only title it carries.
const body = computed(() =>
  props.row ? documentBodyForDisplay(props.row.markdown, props.row.title) : '',
)

const exportMenu = ref<InstanceType<typeof Menu> | null>(null)

/**
 * One menu, three ways out. The entries are the shared list the library row's menu
 * also reads, and the work itself lives in `useAuthoredDocumentExport`, so the
 * document page, the chat panel and the library cannot drift into different exports.
 */
const exportItems = computed(() =>
  AUTHORED_DOCUMENT_EXPORTS.map((option) => ({
    label: t(option.labelKey),
    icon: option.icon,
    command: () => props.row && runExport(props.row, option.kind),
  })),
)
</script>

<style scoped>
.doc-state {
  display: flex;
  justify-content: center;
  padding: 4rem 0;
}
.doc-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}
.doc-meta {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  /* Narrow (the chat panel is about half a split): the row wraps as a whole rather
     than squeezing each chip until its own label breaks across two lines. */
  flex-wrap: wrap;
}
.doc-meta > * {
  flex-shrink: 0;
}
/* The header's actions keep their size when the reader is narrow — in the chat panel
   it is about half a split, and a squeezed button wraps ".md" onto its own line. */
.doc-actions {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}
</style>
